"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { ShieldAlert, Check, X, ClipboardList, Info, Calendar, MessageSquare } from "lucide-react";

interface PendingPosting {
    id: string;
    gig_type_id: string;
    gig_type_name: string;
    owning_entity_type: string;
    owning_entity_id: number;
    owning_entity_name?: string;
    count_needed: number;
    filled_count: number;
    opens_at: string | null;
    closes_at: string | null;
    description: string | null;
    status: string;
    poster_name?: string | null;
    created_at: string | null;
}

interface ConsoleApprovalQueueProps {
    postings: PendingPosting[];
    isLoading: boolean;
    onActionSuccess: () => void;
}

export default function ConsoleApprovalQueue({ postings, isLoading, onActionSuccess }: ConsoleApprovalQueueProps) {
    const router = useRouter();
    const [actioningId, setActioningId] = useState<string | null>(null);
    const [rejectingId, setRejectingId] = useState<string | null>(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const [actionError, setActionError] = useState<string | null>(null);

    const handleApprove = async (id: string) => {
        setActioningId(id);
        setActionError(null);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/console/postings/${id}/approve`, {
                method: "POST"
            });
            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                throw new Error(errBody.detail || "Failed to approve gig posting.");
            }
            onActionSuccess();
        } catch (err: any) {
            console.error("Error approving posting:", err);
            setActionError(err.message || "Failed to approve the opportunity.");
        } finally {
            setActioningId(null);
        }
    };

    const handleReject = async (id: string) => {
        if (!rejectionReason.trim()) {
            setActionError("Please provide a reason for rejecting this posting.");
            return;
        }

        setActioningId(id);
        setActionError(null);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/console/postings/${id}/reject`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason: rejectionReason })
            });
            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                throw new Error(errBody.detail || "Failed to reject gig posting.");
            }
            setRejectingId(null);
            setRejectionReason("");
            onActionSuccess();
        } catch (err: any) {
            console.error("Error rejecting posting:", err);
            setActionError(err.message || "Failed to reject the opportunity.");
        } finally {
            setActioningId(null);
        }
    };

    const formatDate = (dStr: string | null) => {
        if (!dStr) return "N/A";
        return new Date(dStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    if (isLoading) {
        return (
            <div className="py-12 flex flex-col items-center justify-center gap-3">
                <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Loading pending reviews...</p>
            </div>
        );
    }

    if (postings.length === 0) {
        return (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-150 shadow-sm">
                <ClipboardList className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-[#003d45] uppercase tracking-wider">Queue is Empty</h3>
                <p className="text-xs text-gray-400 mt-1">There are no gig postings waiting for Staff review.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">

            {actionError && (
                <div className="bg-red-50 text-red-700 p-4 border border-red-100 rounded-lg text-xs flex items-center gap-2 font-semibold animate-pulse">
                    <Info className="w-4 h-4 text-red-500 shrink-0" />
                    <span><strong>Error:</strong> {actionError}</span>
                </div>
            )}

            {/* Separate Cards List */}
            <div className="flex flex-col gap-4">
                {postings.map((p) => {
                    let mockInterested = p.filled_count;
                    if (mockInterested === 0) {
                        const charSum = p.id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
                        mockInterested = (charSum % 8) + 2; // generates 2-9
                    }

                    let categoryName = "Outreach";
                    if (p.gig_type_name.toLowerCase().includes("webinar") || p.gig_type_name.toLowerCase().includes("rsi")) {
                        categoryName = "Education";
                    } else if (p.gig_type_name.toLowerCase().includes("reference") || p.gig_type_name.toLowerCase().includes("journal") || p.gig_type_name.toLowerCase().includes("cohort")) {
                        categoryName = "Membership";
                    } else if (p.gig_type_name.toLowerCase().includes("challenge") || p.gig_type_name.toLowerCase().includes("mentor") || p.gig_type_name.toLowerCase().includes("alma mater")) {
                        categoryName = "Academia Outreach";
                    }

                    const postedBy = p.poster_name || "Unknown";

                    let eligibility = "Open to all";
                    if (p.gig_type_name.toLowerCase().includes("webinar") || p.gig_type_name.toLowerCase().includes("reference") || p.gig_type_name.toLowerCase().includes("cohort")) {
                        eligibility = "Charterholders";
                    }

                    const categoryStyles: Record<string, string> = {
                        "Academia Outreach": "bg-[#ffd0d6]/30 text-[#ae232d]",
                        "Education": "bg-[#cbeef2]/30 text-[#007a8a]",
                        "Membership": "bg-[#fef3c7]/60 text-[#b45309]",
                        "Outreach": "bg-slate-50 text-slate-700"
                    };

                    const badgeStyle = categoryStyles[categoryName] || "bg-gray-50 text-gray-700";

                    return (
                        <div 
                            key={p.id} 
                            className="bg-white rounded-xl border border-gray-150 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row md:items-center justify-between gap-6 text-left"
                        >
                            {/* Column 1: Details */}
                            <div className="space-y-1">
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <h4 className="text-base font-bold text-[#003d45]">{p.gig_type_name}</h4>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[8.5px] font-bold uppercase tracking-wider ${badgeStyle}`}>
                                        {categoryName}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-400 font-medium">
                                    Posted by <span className="text-gray-500 font-semibold">{postedBy}</span> &middot; {eligibility}
                                </p>
                            </div>

                            {/* Column 2: Status & Actions */}
                            <div className="flex items-center gap-8 self-end md:self-center shrink-0">

                                {/* Action Buttons */}
                                <div className="flex items-center gap-3 shrink-0">
                                    {rejectingId === p.id ? (
                                        <div className="space-y-2 w-full max-w-xs text-left">
                                            <input
                                                type="text"
                                                placeholder="Rejection reason..."
                                                value={rejectionReason}
                                                onChange={(e) => setRejectionReason(e.target.value)}
                                                className="w-full border border-gray-250 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-red-500 font-medium bg-white"
                                                disabled={actioningId === p.id}
                                            />
                                            <div className="flex gap-2 justify-end">
                                                <button
                                                    onClick={() => {
                                                        setRejectingId(null);
                                                        setRejectionReason("");
                                                    }}
                                                    className="px-3 py-1.5 rounded border border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:bg-gray-50 cursor-pointer bg-white"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => handleReject(p.id)}
                                                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-[10px] font-bold uppercase tracking-wider text-white cursor-pointer border-none"
                                                >
                                                    Submit
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => router.push(`/dashboard/gigs/create?id=${p.id}`)}
                                                className="border-2 border-[#004d57] text-[#004d57] bg-white rounded-lg px-4 py-2 font-bold text-xs uppercase tracking-wider hover:bg-gray-50 transition-all cursor-pointer"
                                            >
                                                Edit & approve
                                            </button>
                                            <button
                                                onClick={() => handleApprove(p.id)}
                                                className="bg-[#004d57] text-white rounded-lg px-4 py-2 font-bold text-xs uppercase tracking-wider hover:bg-[#003c44] transition-all border-none cursor-pointer shadow-sm shadow-[#004d57]/10"
                                                disabled={actioningId !== null}
                                            >
                                                {actioningId === p.id ? "Publishing..." : "Approve & publish"}
                                            </button>
                                            <button
                                                onClick={() => setRejectingId(p.id)}
                                                className="text-rose-600 hover:text-rose-800 font-semibold text-[11.5px] cursor-pointer border-none bg-transparent hover:underline"
                                            >
                                                Reject
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
